// Loan calculators: car loan, line of credit, credit card payoff.
import { amortizedPayment, round2 } from "./finance-math";

export function carLoan({ price, downPayment = 0, tradeIn = 0, salesTaxRate = 13, annualRate, termMonths }) {
  const p = Math.max(0, Number(price) || 0);
  const taxAmount = p * (Math.max(0, Number(salesTaxRate) || 0) / 100);
  const financed = Math.max(0, p + taxAmount - (Number(downPayment) || 0) - (Number(tradeIn) || 0));
  const r = Math.max(0, Number(annualRate) || 0) / 100 / 12;
  const n = Math.max(1, Number(termMonths) || 1);
  const payment = amortizedPayment({ principal: financed, periodicRate: r, periods: n });
  const totalPaid = payment * n;
  return {
    monthlyPayment: round2(payment),
    amountFinanced: round2(financed),
    salesTax: round2(taxAmount),
    totalInterest: round2(totalPaid - financed),
    totalCost: round2(totalPaid + (Number(downPayment) || 0) + (Number(tradeIn) || 0)),
  };
}

// Revolving balance payoff (used by line of credit and credit card).
export function payoffByPayment({ balance, apr, monthlyPayment }) {
  let bal = Math.max(0, Number(balance) || 0);
  const r = Math.max(0, Number(apr) || 0) / 100 / 12;
  const pay = Math.max(0, Number(monthlyPayment) || 0);
  const minInterest = bal * r;
  if (pay <= minInterest && r > 0) {
    return { months: null, totalInterest: null, totalPaid: null, neverPaidOff: true, series: [] };
  }
  let months = 0, totalInterest = 0;
  const series = [{ month: 0, balance: round2(bal) }];
  while (bal > 0.01 && months < 1200) {
    const interest = bal * r;
    bal = bal + interest - pay;
    if (bal < 0) bal = 0;
    totalInterest += interest;
    months += 1;
    if (months % 3 === 0 || bal === 0) series.push({ month: months, balance: round2(bal) });
  }
  return {
    months, years: round2(months / 12), totalInterest: round2(totalInterest),
    totalPaid: round2((Number(balance) || 0) + totalInterest), neverPaidOff: false, series,
  };
}

// Credit card: compare a fixed payment vs the "minimum payment" trap.
export function creditCard({ balance, apr, monthlyPayment, minPercent = 3, minFloor = 10 }) {
  const fixed = payoffByPayment({ balance, apr, monthlyPayment });
  // Minimum-payment path: pay max(minPercent% of balance, minFloor) each month.
  let bal = Math.max(0, Number(balance) || 0);
  const r = Math.max(0, Number(apr) || 0) / 100 / 12;
  let months = 0, totalInterest = 0;
  while (bal > 0.01 && months < 1200) {
    const interest = bal * r;
    let pay = Math.max(bal * (minPercent / 100), minFloor);
    if (pay > bal + interest) pay = bal + interest;
    bal = bal + interest - pay;
    if (bal < 0) bal = 0;
    totalInterest += interest;
    months += 1;
  }
  return {
    fixed,
    minimum: { months, years: round2(months / 12), totalInterest: round2(totalInterest) },
  };
}
